import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import logo from '../logo.svg';

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import Alert from './Alert';
import PropTypes from 'prop-types';

const VerifyOTP = ({ onLogin, onResendOTP }) => {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [showNameInput, setShowNameInput] = useState(false);
    const [name, setName] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const phoneNumber = location.state?.phoneNumber;

    useEffect(() => {
        if (!phoneNumber) {
            navigate('/');
        }
        const timer = resendTimer > 0 && setInterval(() => setResendTimer(resendTimer - 1), 1000);
        return () => clearInterval(timer);
    }, [resendTimer, navigate, phoneNumber]);

    const onOTPVerify = () => {
        if (!otp) {
            setError('Silakan masukkan kode OTP Anda.');
            return;
        }
        setLoading(true);

        if (!window.confirmationResult) {
            setError('Sesi verifikasi tidak ditemukan. Silakan coba lagi.');
            setLoading(false);
            return;
        }

        window.confirmationResult.confirm(otp).then(async (res) => {
            const user = res.user;
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                setShowNameInput(true);
            } else {
                onLogin(user);
                navigate('/');
            }
            setLoading(false);
        }).catch((err) => {
            if (err.code === 'auth/code-expired') {
                setError('Kode OTP sudah kedaluwarsa, silakan minta kode baru.');
            } else if (err.code === 'auth/invalid-verification-code') {
                setError('Kode OTP salah, silakan coba lagi.');
            } else {
                setError('Terjadi kesalahan, silakan coba lagi.');
            }
            setLoading(false);
        });
    }

    const handleResendOTP = async () => {
        if (!window.confirmationResult) {
            setError('Sesi verifikasi tidak ditemukan. Silakan coba lagi.');
            return;
        }
        setLoading(true);
        try {
            await onResendOTP();
            setResendTimer(60);
        } catch (error) {
            setError('Gagal mengirim ulang OTP. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!name) {
            setError('Silakan masukkan nama Anda.');
            return;
        }
        setLoading(true);
        setError(null);

        const user = auth.currentUser;
        if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
                name: name,
                phoneNumber: phoneNumber,
                roles: ['member'],
                phoneNumberVerifiedAt: new Date()
            });
            onLogin(user);
            navigate('/');
        }
        setLoading(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onOTPVerify();
        }
    }

    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCreateUser();
        }
    }

    return (
        <div className="flex items-center justify-center h-screen bg-brand-dark">
            <div className="w-full max-w-md p-8 space-y-6 bg-brand-gray rounded-lg shadow-md">
                <Link to="/">
                    <img src={logo} className="w-20 h-20 mx-auto" alt="logo" />
                </Link>
                <h2 className="text-2xl font-bold text-center text-white">Verifikasi OTP</h2>
                {error && <Alert message={error} />}
                <p className="text-center text-gray-400">Silakan masukkan kode OTP yang Anda terima di {phoneNumber}.</p>
                {showNameInput ? (
                    <div className="space-y-4">
                        <div>
                            <input
                                type="text"
                                placeholder="Masukkan Nama"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setError(null);
                                }}
                                onKeyDown={handleNameKeyDown}
                                className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-lime"
                            />
                        </div>
                        <button
                            onClick={handleCreateUser}
                            disabled={loading}
                            className="w-full px-4 py-2 font-bold text-brand-dark bg-brand-lime rounded-md hover:bg-lime-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime disabled:opacity-50"
                        >
                            {loading ? 'Membuat Akun...' : 'Buat Akun'}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    placeholder="Masukkan OTP"
                                    value={otp}
                                    onChange={(e) => {
                                        setOtp(e.target.value);
                                        setError(null);
                                    }}
                                    onKeyDown={handleKeyDown}
                                    className="w-full px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-lime"
                                />
                            </div>
                        </div>
                        <button
                            onClick={onOTPVerify}
                            disabled={loading}
                            className="w-full px-4 py-2 font-bold text-brand-dark bg-brand-lime rounded-md hover:bg-lime-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime disabled:opacity-50"
                        >
                            {loading ? 'Memverifikasi...' : 'Verifikasi OTP'}
                        </button>
                        <div className="text-center">
                            <button
                                onClick={handleResendOTP}
                                disabled={resendTimer > 0 || loading}
                                className="text-sm text-brand-lime disabled:text-gray-500"
                            >
                                {loading ? 'Mengirim ulang...' : resendTimer > 0 ? `Kirim ulang OTP dalam ${resendTimer} detik` : 'Kirim ulang OTP'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

VerifyOTP.propTypes = {
    onLogin: PropTypes.func.isRequired,
    onResendOTP: PropTypes.func.isRequired,
};

VerifyOTP.defaultProps = {
    onLogin: () => { },
};

export default VerifyOTP;
